// src/jobs.schema.ts
import { z } from "zod";
var WORKSPACE_VERSION_GROUPS = [
  "stable",
  "candidate",
  "dev"
];
var JobStatus = /* @__PURE__ */ function(JobStatus2) {
  JobStatus2["ACTIVE"] = "ACTIVE";
  JobStatus2["PAUSED"] = "PAUSED";
  return JobStatus2;
}({});
var customDatabaseFieldSchema = z.preprocess((val) => {
  if (val instanceof Date) {
    return val.toISOString();
  }
  return val;
}, z.iso.datetime());
var jobFrequencySchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("ONCE"),
    at: z.iso.datetime().optional()
  }),
  z.object({
    type: z.literal("RECURRING"),
    every: z.iso.duration(),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional()
  })
]);
var commonJobDataSchema = z.object({
  image: z.string().optional(),
  sentry_monitor: z.string().optional()
});
var etlDataSchema = commonJobDataSchema.extend({
  rs_database: z.string(),
  schema: z.string(),
  wf_ids: z.array(z.number()),
  filters: z.array(z.string()).optional(),
  column_filter: z.array(z.string()).optional()
});
var compilerWorkspaceVersionSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  type: z.string(),
  group: z.enum(WORKSPACE_VERSION_GROUPS),
  createdAt: z.string()
}).strict();
var compilerWorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  version: compilerWorkspaceVersionSchema.nullable(),
  tags: z.record(z.string(), z.union([
    z.string(),
    z.number(),
    z.boolean()
  ])),
  processId: z.string(),
  /**
   * Account row ID of the user who created the workspace.
   */
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  published_workflow_ids: z.record(z.string(), z.string()).optional()
}).strict();
var compilerDataSchema = commonJobDataSchema.extend({
  workflows: z.record(z.string(), z.string()).optional(),
  repositories: z.record(z.string(), z.any()).optional(),
  workspace: compilerWorkspaceSchema.optional(),
  organizationName: z.string().optional(),
  /**
  * Environment variables injected into the compiler job. Optional at parse
  * time (defaults to `{}` for backward compatibility with previously enqueued
  * jobs that predate the env-var feature) but always defined after `parse()`.
  * Note the input/output asymmetry: constructing a `CompilerJobData` literal
  * still requires `envVars: {}` if you have none, because the inferred output
  * type lists the field as required.
  */
  envVars: z.record(z.string(), z.string()).optional().default({})
});
var webhookDataSchema = commonJobDataSchema.extend({
  webhook_url: z.string(),
  webhook_payload: z.record(z.string(), z.any()).optional(),
  authenticationParams: z.object({
    impersonateStratumnEmail: z.email().optional()
  }).optional()
});
var zipDataSchema = commonJobDataSchema.extend({
  endpointRoute: z.string(),
  endpointMethod: z.string(),
  queryParams: z.record(z.string(), z.string()).optional(),
  traceId: z.string(),
  workflowId: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string()
  })
});
var commonCreateJobSchema = z.object({
  name: z.string(),
  frequency: jobFrequencySchema,
  maxRetry: z.number().int().min(0).default(3)
});
var createWebhookJobSchema = commonCreateJobSchema.extend({
  type: z.literal("WEBHOOK"),
  data: webhookDataSchema
});
var createCompilerJobSchema = commonCreateJobSchema.extend({
  type: z.literal("COMPILER"),
  data: compilerDataSchema
});
var createEtlJobSchema = commonCreateJobSchema.extend({
  type: z.literal("ETL"),
  data: etlDataSchema
});
var createZipJobSchema = commonCreateJobSchema.extend({
  type: z.literal("ZIP"),
  data: zipDataSchema
});
var createJobSchema = z.discriminatedUnion("type", [
  createEtlJobSchema,
  createCompilerJobSchema,
  createWebhookJobSchema,
  createZipJobSchema
]);
var baseUpdateJobSchema = z.object({
  status: z.enum(JobStatus).optional(),
  name: z.string().optional(),
  frequency: jobFrequencySchema.optional(),
  maxRetry: z.number().int().min(0).optional()
});
var updateWebhookJobPayloadSchema = baseUpdateJobSchema.extend({
  type: z.literal("WEBHOOK"),
  data: webhookDataSchema.optional()
});
var updateCompilerJobPayloadSchema = baseUpdateJobSchema.extend({
  type: z.literal("COMPILER"),
  data: compilerDataSchema.optional()
});
var updateEtlJobPayloadSchema = baseUpdateJobSchema.extend({
  type: z.literal("ETL"),
  data: etlDataSchema.optional()
});
var updateZipJobPayloadSchema = baseUpdateJobSchema.extend({
  type: z.literal("ZIP"),
  data: zipDataSchema.optional()
});
var updateJobSchema = z.discriminatedUnion("type", [
  updateEtlJobPayloadSchema,
  updateCompilerJobPayloadSchema,
  updateWebhookJobPayloadSchema,
  updateZipJobPayloadSchema
]);
var JobExecutionStatus = /* @__PURE__ */ function(JobExecutionStatus2) {
  JobExecutionStatus2["PENDING"] = "PENDING";
  JobExecutionStatus2["RUNNING"] = "RUNNING";
  JobExecutionStatus2["COMPLETED"] = "COMPLETED";
  JobExecutionStatus2["FAILED"] = "FAILED";
  return JobExecutionStatus2;
}({});
var jobExecutionSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  status: z.enum(JobExecutionStatus),
  executionTime: customDatabaseFieldSchema.nullish(),
  retryCount: z.number(),
  logs: z.string().nullish(),
  metrics: z.string().nullish(),
  lastUpdatedTime: customDatabaseFieldSchema
});
var jobNextExecutionSchema = z.object({
  jobId: z.string(),
  nextExecutionTime: customDatabaseFieldSchema
});
var commonJobSchema = z.object({
  id: z.string(),
  status: z.enum(JobStatus),
  createdAt: customDatabaseFieldSchema,
  updatedAt: customDatabaseFieldSchema,
  createdBy: z.string(),
  deletedAt: customDatabaseFieldSchema.nullish(),
  jobExecutions: z.array(jobExecutionSchema).optional(),
  jobNextExecution: jobNextExecutionSchema.nullish()
});
var etlJobSchema = createEtlJobSchema.extend(commonJobSchema);
var compilerJobSchema = createCompilerJobSchema.extend(commonJobSchema);
var webhookJobSchema = createWebhookJobSchema.extend(commonJobSchema);
var zipJobSchema = createZipJobSchema.extend(commonJobSchema);
var jobSchema = z.discriminatedUnion("type", [
  etlJobSchema,
  compilerJobSchema,
  webhookJobSchema,
  zipJobSchema
]);
var getJobParamsSchema = z.object({
  page: z.number().optional(),
  take: z.number().optional()
});
var jobDataInsideQueueSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("ETL"),
    id: z.string(),
    frequency: jobFrequencySchema,
    data: etlDataSchema,
    jobExecutionId: z.string()
  }),
  z.object({
    type: z.literal("COMPILER"),
    id: z.string(),
    frequency: jobFrequencySchema,
    data: compilerDataSchema,
    jobExecutionId: z.string()
  }),
  z.object({
    type: z.literal("WEBHOOK"),
    id: z.string(),
    frequency: jobFrequencySchema,
    data: webhookDataSchema,
    jobExecutionId: z.string()
  }),
  z.object({
    type: z.literal("ZIP"),
    id: z.string(),
    frequency: jobFrequencySchema,
    data: zipDataSchema,
    jobExecutionId: z.string()
  })
]);
var updateJobExecutionSchema = z.object({
  metrics: z.string().optional(),
  logs: z.string().optional()
});
export {
  JobExecutionStatus,
  JobStatus,
  compilerDataSchema,
  compilerJobSchema,
  createCompilerJobSchema,
  createEtlJobSchema,
  createJobSchema,
  createWebhookJobSchema,
  createZipJobSchema,
  customDatabaseFieldSchema,
  etlDataSchema,
  etlJobSchema,
  jobDataInsideQueueSchema,
  jobExecutionSchema,
  jobFrequencySchema,
  jobNextExecutionSchema,
  jobSchema,
  updateJobExecutionSchema,
  updateJobSchema,
  webhookDataSchema,
  webhookJobSchema,
  zipDataSchema,
  zipJobSchema
};
//# sourceMappingURL=index.mjs.map